import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Listbox } from "@headlessui/react";
import { ChevronDownIcon, MapPinIcon } from "@heroicons/react/24/solid";
import classNames from "classnames";

import WaterIcon from "@components/icons/WaterIcon";
import Loading from "@components/shared/Loading";
import Card from "@components/zenith/ui/Card";
import { useGetDamLevels } from "@hooks/query/zenith/useGetDamLevels";
import type { Coordinates, DamLevel } from "@services/zenith/damLevels";
import {
  DEFAULT_LOCATION,
  useSubscriberLocations,
} from "./useSubscriberLocations";

// The level circle: 230 px across, its water inside a 6 px rim.
const SIZE = 230;
const R = SIZE / 2;

// Seqwater observes in Queensland time and writes it "2:37pm 08/10/2026"; so does the card, wherever its viewer is.
const observedAt = (iso: string) => {
  const part = Object.fromEntries(
    new Intl.DateTimeFormat("en-AU", {
      timeZone: "Australia/Brisbane",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
      .formatToParts(new Date(iso))
      .map(({ type, value }) => [type, value]),
  );
  return `${part.hour}:${part.minute}${part.dayPeriod.toLowerCase()} ${part.day}/${part.month}/${part.year}`;
};

/**
 * How full a Seqwater dam is: My dam, or another the subscriber picks. My dam is the dam nearest the subscriber
 * location, or nearest the default location (Brisbane) when the subscriber location is only a town.
 */
const DamLevelWidget: React.FunctionComponent = () => {
  const { t, i18n } = useTranslation();
  const { locations, isError: isLocationsError } = useSubscriberLocations();
  // A town-only subscriber location has no coordinates, and failed locations have none at all: the tenant's default
  // location stands in, so the card still shows a dam.
  const near = isLocationsError
    ? (DEFAULT_LOCATION.place as Coordinates)
    : locations &&
      ((locations.find(l => "lat" in l.place)?.place ??
        DEFAULT_LOCATION.place) as Coordinates);
  const { data: dams, isError } = useGetDamLevels(near);
  // Every dam arrives in one list, so picking another needs no request.
  const [chosenId, setChosenId] = useState<string>();
  const dam = dams?.find(d => d.id === chosenId) ?? dams?.[0];

  return (
    <Card
      title={
        <span className="flex items-center gap-2.5">
          <WaterIcon className="h-5 w-5 text-primary-1" />
          {t("zenith.dashboard.dam.title")}
        </span>
      }
    >
      {dam ? (
        <Level dam={dam} lang={i18n.resolvedLanguage ?? "en"}>
          <DamPicker dams={dams!} current={dam} onChoose={setChosenId} />
        </Level>
      ) : isError ? (
        <p className="px-5 py-4 text-sm text-gray-500">
          {t("zenith.dashboard.dam.unavailable")}
        </p>
      ) : (
        <Loading />
      )}
    </Card>
  );
};

const DamPicker: React.FunctionComponent<{
  dams: DamLevel[];
  current: DamLevel;
  onChoose: (id: string) => void;
}> = ({ dams, current, onChoose }) => {
  const { t } = useTranslation();

  return (
    <Listbox value={current.id} onChange={onChoose}>
      <div className="relative mt-3">
        <Listbox.Label className="sr-only">
          {t("zenith.dashboard.dam.change_dam")}
        </Listbox.Label>
        <Listbox.Button className="flex items-center gap-2 rounded font-bold text-primary-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-1">
          <MapPinIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {current.name}
          <ChevronDownIcon className="h-3 w-3 opacity-70" aria-hidden="true" />
        </Listbox.Button>
        <Listbox.Options className="absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 max-h-72 w-64 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
          {dams.map(dam => (
            <Listbox.Option
              key={dam.id}
              value={dam.id}
              className={({ active, selected }) =>
                classNames(
                  "cursor-pointer px-4 py-2",
                  { "bg-gray-50": active },
                  selected ? "font-semibold text-primary-1" : "text-gray-900",
                )
              }
            >
              {dam.name}
            </Listbox.Option>
          ))}
        </Listbox.Options>
      </div>
    </Listbox>
  );
};

const Level: React.FunctionComponent<{
  dam: DamLevel;
  lang: string;
  /** The dam picker, under the figures. */
  children: React.ReactNode;
}> = ({ dam, lang, children }) => {
  const { t } = useTranslation();
  // Unique per card, without useId's colons, which break url(#…) references.
  const id = useId().replace(/:/g, "");
  // The water stops at the brim; the figure doesn't, as a spilling dam is over 100%.
  const waterline = SIZE * (1 - Math.min(dam.percentFull, 100) / 100);
  const percent = new Intl.NumberFormat(lang, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(dam.percentFull);
  const megalitres = (litres: number) =>
    `${new Intl.NumberFormat(lang, { maximumFractionDigits: 0 }).format(
      litres / 1e6,
    )} ${t("common.volume_unit")}`;
  // Drawn twice, white clipped to the water and navy clipped to the air, so it reads at any level.
  const figure = (className: string) => (
    <text
      x={R}
      y={R}
      textAnchor="middle"
      dominantBaseline="central"
      className={`text-5xl font-bold ${className}`}
    >
      {percent}
      <tspan className="text-lg" dy="-1.2em">
        %
      </tspan>
    </text>
  );

  return (
    <div className="flex flex-col items-center px-4 pb-4 pt-2">
      <div
        role="meter"
        aria-label={t("zenith.dashboard.dam.level") as string}
        aria-valuenow={dam.percentFull}
        aria-valuemin={0}
        aria-valuemax={Math.max(100, dam.percentFull)}
        aria-valuetext={`${percent}%`}
      >
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          aria-hidden="true"
        >
          <defs>
            <clipPath id={`${id}-inside`}>
              <circle cx={R} cy={R} r={R - 6} />
            </clipPath>
            <clipPath id={`${id}-water`}>
              <rect x="0" y={waterline} width={SIZE} height={SIZE} />
            </clipPath>
            <clipPath id={`${id}-air`}>
              <rect x="0" y="0" width={SIZE} height={waterline} />
            </clipPath>
          </defs>
          <circle
            cx={R}
            cy={R}
            r={R - 3}
            strokeWidth="5"
            className="fill-gray-100 stroke-gray-300"
          />
          <rect
            x="0"
            y={waterline}
            width={SIZE}
            height={SIZE}
            clipPath={`url(#${id}-inside)`}
            // sky-600: white large text on it passes WCAG AA, unlike the design's lighter blue.
            className="fill-sky-600"
          />
          <g clipPath={`url(#${id}-air)`}>{figure("fill-primary-3")}</g>
          <g clipPath={`url(#${id}-water)`}>{figure("fill-white")}</g>
        </svg>
      </div>

      <div className="relative mt-3 rounded bg-gray-100 px-4 py-3 text-xs text-gray-900">
        <span
          aria-hidden="true"
          className="absolute -top-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 bg-gray-100"
        />
        <dl className="table border-separate border-spacing-y-0.5">
          <div className="table-row">
            <dt className="table-cell pr-3.5 font-bold">
              {t("zenith.dashboard.dam.full_supply_volume")}
            </dt>
            <dd className="table-cell">{megalitres(dam.fullSupplyVolume)}</dd>
          </div>
          <div className="table-row">
            <dt className="table-cell pr-3.5 font-bold">
              {t("zenith.dashboard.dam.current_volume")}
            </dt>
            <dd className="table-cell">{megalitres(dam.currentVolume)}</dd>
          </div>
        </dl>
        <p className="mt-1 text-right text-gray-500">
          {t("zenith.dashboard.dam.observed_at", {
            time: observedAt(dam.observedAt),
          })}
        </p>
        {dam.comment ? (
          <p className="mt-1 font-semibold text-sky-700">{dam.comment}</p>
        ) : null}
      </div>

      {children}
      <a
        href="https://www.seqwater.com.au/dam-levels"
        target="_blank"
        rel="noreferrer"
        className="mt-2 text-xs text-gray-500 hover:underline"
      >
        {t("zenith.dashboard.dam.source")}
      </a>
    </div>
  );
};

export default DamLevelWidget;
